/**
 * Equipe do condomínio — gerida pelo SÍNDICO (ou pelo Dono), no escopo do próprio
 * condomínio. Cadastrar usuários (subsíndico/conselho/morador), trocar papel,
 * redefinir senha, remover e ajustar permissões pontuais (concessões).
 *
 * Reusa os endpoints escopados: /api/condominios/{cid}/equipe...
 *
 * 0.1.82: cartões AGRUPADOS POR APARTAMENTO — o morador do cadastro de unidades
 * (principal) sempre primeiro e, no cartão dele, "Adicionar morador deste apto"
 * (cônjuge, filhos...), que liga a pessoa ao apartamento (vinculo.unidade).
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle, ArrowLeft, Check, Eye, Home, KeyRound, Loader2, Lock, Mail, Phone, Plus, ShieldCheck, SlidersHorizontal, Trash2, UserPlus, Users, X,
} from "lucide-react";
import { api, ApiError } from "../lib/api";
import { usePermissoes } from "../lib/permissoes";

interface Membro {
  id: number; nome: string; email: string; telefone?: string; telefone_cadastro?: string; papel: string; apto: string | null;
  vinculo_id: number; ativo: boolean; is_dono: boolean;
  concessoes: Record<string, { pode_ler: boolean | null; pode_escrever: boolean | null }>;
  /** 0.1.82: contato do apto no cadastro de unidades (vem primeiro no grupo). */
  principal?: boolean;
  /** 0.1.82: morador adicional ligado ao apto pelo vínculo. */
  adicional?: boolean;
  /** 0.1.82: quantidade de moradores informada no cadastro do apto (0 = não informada). */
  qtd_moradores_cadastro?: number;
  /** 0.1.83: relação com o apto: proprietario | inquilino | morador. */
  relacao?: string | null;
  /** 0.1.83: ocupação do apto no cadastro: proprio | alugado. */
  ocupacao?: string;
  /** 0.1.83: proprietário de apto alugado (não mora lá). */
  nao_reside?: boolean;
  /** 0.1.87: autorizou receber os avisos pelo WhatsApp neste condomínio. */
  whatsapp?: boolean;
}

const RELACOES: { chave: string; rotulo: string }[] = [
  { chave: "proprietario", rotulo: "Proprietário" },
  { chave: "inquilino", rotulo: "Inquilino" },
  { chave: "morador", rotulo: "Morador (familiar/dependente)" },
];
const SELO_RELACAO: Record<string, { txt: string; cls: string; dica: string }> = {
  proprietario: { txt: "PROPRIETÁRIO", cls: "text-emerald-700 bg-emerald-50 border-emerald-200",
    dica: "Vota nas assembleias e vê a prestação de contas." },
  inquilino: { txt: "INQUILINO", cls: "text-sky-700 bg-sky-50 border-sky-200",
    dica: "Usa o dia a dia (visitas, encomendas, reservas) e vê o boleto. Vota só com procuração do proprietário." },
  morador: { txt: "MORADOR", cls: "text-slate-600 bg-slate-100 border-slate-200",
    dica: "Familiar/dependente. Vota só com procuração do proprietário." },
};

/** Agrupa por apartamento: principal primeiro, depois os demais por nome. */
function agruparPorApto(equipe: Membro[]): { semApto: Membro[]; grupos: [string, Membro[]][] } {
  const semApto: Membro[] = [];
  const mapa = new Map<string, Membro[]>();
  for (const m of equipe) {
    if (!m.apto) { semApto.push(m); continue; }
    const lista = mapa.get(m.apto) ?? [];
    lista.push(m);
    mapa.set(m.apto, lista);
  }
  const grupos = [...mapa.entries()]
    .sort(([a], [b]) => a.localeCompare(b, "pt-BR", { numeric: true }))
    .map(([apto, lista]): [string, Membro[]] => [apto, [...lista].sort((x, y) =>
      Number(!!y.principal) - Number(!!x.principal) || x.nome.localeCompare(y.nome, "pt-BR"))]);
  return { semApto, grupos };
}
interface Papel { chave: string; rotulo: string }
interface Func { chave: string; rotulo: string }
interface Resp { equipe: Membro[]; papeis: Papel[]; funcionalidades: Func[] }

interface CelulaPadrao { papel: string; funcionalidade: string; pode_ler: boolean; pode_escrever: boolean }
interface PadraoResp { matriz: CelulaPadrao[]; papeis: Papel[]; funcionalidades: Func[] }

export function EquipePage({ condominioId, onVoltar }: { condominioId: number; onVoltar: () => void }) {
  const qc = useQueryClient();
  const chave = ["equipe", condominioId];
  const inval = () => qc.invalidateQueries({ queryKey: chave });

  const { data, isLoading, error } = useQuery<Resp>({
    queryKey: chave,
    queryFn: () => api.get<Resp>(`/condominios/${condominioId}/equipe`),
  });

  const [nome, setNome] = useState(""); const [email, setEmail] = useState("");
  const [senha, setSenha] = useState(""); const [papel, setPapel] = useState("morador");
  const [telefone, setTelefone] = useState("");
  const [erro, setErro] = useState<string | null>(null);

  // Consulta (só-leitura) da matriz de permissões PADRÃO — para o síndico saber o que já
  // é padrão antes de conceder exceções. A edição do padrão continua exclusiva do Dono.
  const [verPadrao, setVerPadrao] = useState(false);
  const { data: padrao, isLoading: padraoLoading } = useQuery<PadraoResp>({
    queryKey: ["permissoes-padrao", condominioId],
    queryFn: () => api.get<PadraoResp>(`/condominios/${condominioId}/permissoes-padrao`),
    enabled: verPadrao,
  });

  const [aviso, setAviso] = useState<string | null>(null);
  const criar = useMutation({
    mutationFn: () => api.post<{ ja_existia?: boolean }>(`/condominios/${condominioId}/equipe`, { nome, email, senha, papel, telefone }),
    onSuccess: (r) => {
      setAviso(r?.ja_existia ? `${email.trim().toLowerCase()} já tinha cadastro no SíndicoApp: foi incluído, mas continua entrando com a senha que já usava (a senha digitada não foi usada). Para trocar, use "Redefinir senha" no cartão dele.` : null);
      setNome(""); setEmail(""); setSenha(""); setTelefone(""); setErro(null); inval();
    },
    onError: (e) => setErro(e instanceof ApiError ? e.message : "Não foi possível adicionar."),
  });

  const [impMsg, setImpMsg] = useState<string | null>(null);
  const importar = useMutation({
    mutationFn: () => api.post<{ total_unidades: number; criados: number; ja_tinham_acesso: number; sem_email: number; sindicos_marcados?: number; nomes_atualizados?: number; emails_atualizados?: number }>(
      `/condominios/${condominioId}/equipe/importar-unidades`, {}),
    onSuccess: (r) => {
      setImpMsg(
        `Importação concluída: ${r.criados} novo(s) condômino(s), ${r.ja_tinham_acesso} já tinham acesso ` +
        `(de ${r.total_unidades} unidades).` +
        (r.sindicos_marcados ? ` ${r.sindicos_marcados} já entrou como Síndico (contato bate com o cadastro).` : "") +
        (r.nomes_atualizados ? ` ${r.nomes_atualizados} nome(s) atualizado(s) a partir do cadastro.` : "") +
        (r.emails_atualizados ? ` ${r.emails_atualizados} e-mail(s) real(is) aplicado(s) a acessos que estavam sem e-mail.` : "") +
        (r.sem_email ? ` ${r.sem_email} sem e-mail — completar o e-mail depois para eles acessarem.` : ""),
      );
      inval();
    },
    onError: (e) => setImpMsg(e instanceof ApiError ? e.message : "Não foi possível importar."),
  });

  if (error instanceof ApiError && error.status === 403) {
    return (
      <div className="max-w-3xl mx-auto">
        <Voltar onVoltar={onVoltar} />
        <p className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg p-4">
          Você não tem permissão para gerir a equipe deste condomínio.
        </p>
      </div>
    );
  }

  const papeis = data?.papeis ?? [];
  const funcs = data?.funcionalidades ?? [];

  return (
    <div className="max-w-3xl mx-auto">
      <Voltar onVoltar={onVoltar} />
      <div className="flex items-start justify-between gap-2 mb-1">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-xl bg-brand-700 text-white flex items-center justify-center"><KeyRound size={20} /></div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Equipe do condomínio</h1>
            <p className="text-sm text-slate-500">Cadastre usuários, defina o papel e ajuste os acessos deste condomínio.</p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setVerPadrao(true)}
          title="Ver (somente leitura) o que cada papel já enxerga por padrão. A edição do padrão é do Dono."
          className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-md border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 shrink-0"
        >
          <Eye size={14} /> Ver perfil padrão
        </button>
      </div>

      {/* Novo usuário */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 mt-4">
        <h3 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-1.5"><UserPlus size={16} /> Adicionar pessoa</h3>
        <div className="grid grid-cols-1 sm:grid-cols-6 gap-2">
          <input placeholder="Nome" value={nome} onChange={(e) => setNome(e.target.value)}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-brand-500" />
          <input placeholder="E-mail" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-brand-500" />
          <input placeholder="Telefone (opcional)" value={telefone} onChange={(e) => setTelefone(e.target.value)}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-brand-500" />
          <input placeholder="Senha inicial" type="text" value={senha} onChange={(e) => setSenha(e.target.value)}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-brand-500" />
          <select value={papel} onChange={(e) => setPapel(e.target.value)}
            className="rounded-lg border border-slate-300 px-2 py-2 text-sm outline-none bg-white">
            {papeis.map((p) => <option key={p.chave} value={p.chave}>{p.rotulo}</option>)}
          </select>
          <button disabled={!nome || !email || !senha || criar.isPending} onClick={() => criar.mutate()}
            className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-brand-700 text-white px-3 py-2 text-sm font-medium hover:bg-brand-800 disabled:opacity-50">
            {criar.isPending ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} Adicionar
          </button>
        </div>
        <p className="text-[11px] text-slate-500 mt-2">
          Papéis: <b>Subsíndico</b> e <b>Conselho</b> (leitura ampliada) e <b>Condômino/Morador</b>
          (só leitura do essencial). O síndico não cria outro síndico nem o administrador.
        </p>
        {erro && <div className="mt-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded px-3 py-2">{erro}</div>}
        {aviso && <div className="mt-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">{aviso}</div>}

        {/* Importar do cadastro de unidades (economiza digitação em prédios grandes) */}
        <div className="mt-3 border-t border-slate-100 pt-3">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => { setImpMsg(null); importar.mutate(); }}
              disabled={importar.isPending}
              className="inline-flex items-center gap-1.5 rounded-lg border border-brand-300 text-brand-700 hover:bg-brand-50 px-3 py-2 text-sm font-medium disabled:opacity-50"
            >
              {importar.isPending ? <Loader2 size={15} className="animate-spin" /> : <UserPlus size={15} />}
              Importar condôminos do cadastro de unidades
            </button>
            <span className="text-[11px] text-slate-500">
              Traz todos os apartamentos como <b>condômino</b>. Depois você só ajusta quem é
              subsíndico/conselho.
            </span>
          </div>
          {impMsg && <div className="mt-2 text-[12px] text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-3 py-2">{impMsg}</div>}
        </div>
      </div>

      {/* Lista da equipe */}
      {isLoading ? (
        <div className="text-sm text-slate-400 flex items-center gap-2 mt-4"><Loader2 size={16} className="animate-spin" /> Carregando…</div>
      ) : (
        <div className="space-y-3 mt-5">
          {(data?.equipe ?? []).length === 0 && (
            <p className="text-sm text-slate-500">Ninguém vinculado a este condomínio ainda.</p>
          )}
          {(() => {
            const { semApto, grupos } = agruparPorApto(data?.equipe ?? []);
            return (
              <>
                {semApto.length > 0 && grupos.length > 0 && (
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500 pt-1">Equipe e demais acessos</h3>
                )}
                {semApto.map((m) => (
                  <MembroCard key={m.id} m={m} cid={condominioId} papeis={papeis} funcs={funcs} inval={inval} />
                ))}
                {grupos.length > 0 && (
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500 pt-3">Moradores por apartamento</h3>
                )}
                {grupos.map(([apto, membros]) => (
                  <GrupoApto key={apto} apto={apto} membros={membros} cid={condominioId}
                    papeis={papeis} funcs={funcs} inval={inval} />
                ))}
              </>
            );
          })()}
        </div>
      )}

      {verPadrao && (
        <PerfilPadraoModal padrao={padrao} carregando={padraoLoading} onFechar={() => setVerPadrao(false)} />
      )}
    </div>
  );
}

/** Consulta (só-leitura) da matriz de permissões padrão. O síndico vê e sai. */
function PerfilPadraoModal({ padrao, carregando, onFechar }: {
  padrao?: PadraoResp; carregando: boolean; onFechar: () => void;
}) {
  const papeis = padrao?.papeis ?? [];
  const funcs = padrao?.funcionalidades ?? [];
  const mapa = new Map<string, CelulaPadrao>();
  for (const c of padrao?.matriz ?? []) mapa.set(`${c.papel}|${c.funcionalidade}`, c);
  const rotuloCelula = (c?: CelulaPadrao) =>
    !c || (!c.pode_ler && !c.pode_escrever) ? "—" : c.pode_escrever ? "Ler+Editar" : "Ler";
  const corCelula = (c?: CelulaPadrao) =>
    !c || (!c.pode_ler && !c.pode_escrever) ? "text-slate-300"
      : c.pode_escrever ? "text-emerald-700 font-semibold" : "text-sky-700";

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={onFechar}>
      <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between gap-2 p-4 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <Eye size={18} className="text-brand-700" />
            <div>
              <h3 className="font-bold text-slate-900">Perfil padrão de acessos (consulta)</h3>
              <p className="text-xs text-slate-500">Somente leitura — o que cada papel já enxerga por padrão. A edição do padrão é do Dono.</p>
            </div>
          </div>
          <button onClick={onFechar} className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100"><X size={18} /></button>
        </div>
        <div className="p-4 overflow-auto">
          {carregando ? (
            <div className="text-sm text-slate-400 flex items-center gap-2 py-8 justify-center"><Loader2 size={16} className="animate-spin" /> Carregando…</div>
          ) : (
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr>
                  <th className="text-left p-2 sticky left-0 bg-white">Funcionalidade</th>
                  {papeis.map((p) => <th key={p.chave} className="p-2 text-center font-semibold text-slate-700">{p.rotulo}</th>)}
                </tr>
              </thead>
              <tbody>
                {funcs.map((f, i) => (
                  <tr key={f.chave} className={i % 2 ? "bg-slate-50" : ""}>
                    <td className="p-2 text-slate-700 sticky left-0 bg-inherit">{f.rotulo}</td>
                    {papeis.map((p) => {
                      const c = mapa.get(`${p.chave}|${f.chave}`);
                      return <td key={p.chave} className={`p-2 text-center ${corCelula(c)}`}>{rotuloCelula(c)}</td>;
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <p className="text-[11px] text-slate-400 mt-3">
            <b>Ler</b> = vê a área. <b>Ler+Editar</b> = vê e altera. <b>—</b> = sem acesso.
            Exceções por pessoa (concessões) você ajusta nos cartões da equipe.
          </p>
        </div>
        <div className="p-3 border-t border-slate-200 flex justify-end">
          <button onClick={onFechar} className="px-4 py-2 rounded-md bg-slate-800 text-white text-sm hover:bg-slate-900">Fechar</button>
        </div>
      </div>
    </div>
  );
}

function Voltar({ onVoltar }: { onVoltar: () => void }) {
  return (
    <button onClick={onVoltar} className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 mb-4">
      <ArrowLeft size={16} /> Voltar
    </button>
  );
}

/** Um apartamento: o morador do cadastro primeiro (com o botão de adicionar) e os
 *  moradores adicionais logo abaixo, recuados. */
function GrupoApto({ apto, membros, cid, papeis, funcs, inval }: {
  apto: string; membros: Membro[]; cid: number; papeis: Papel[]; funcs: Func[]; inval: () => void;
}) {
  const principal = membros.find((m) => m.principal) ?? null;
  const outros = membros.filter((m) => m !== principal);
  const qtd = membros.find((m) => (m.qtd_moradores_cadastro ?? 0) > 0)?.qtd_moradores_cadastro ?? 0;
  const adicionar = <AdicionarMoradorApto apto={apto} cid={cid} inval={inval} total={membros.length} qtd={qtd} />;
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-2 space-y-2">
      {principal ? (
        <MembroCard m={principal} cid={cid} papeis={papeis} funcs={funcs} inval={inval} rodape={adicionar} />
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl p-3">
          <div className="text-sm font-semibold text-slate-800 flex items-center gap-1.5"><Home size={15} /> Apto {apto}</div>
          <p className="text-[11px] text-slate-500 mt-0.5">O contato deste apto no cadastro de unidades ainda não tem acesso.</p>
          {adicionar}
        </div>
      )}
      {outros.map((m) => (
        <div key={m.id} className="ml-5 border-l-2 border-brand-200 pl-2">
          <MembroCard m={m} cid={cid} papeis={papeis} funcs={funcs} inval={inval} />
        </div>
      ))}
    </div>
  );
}

/** "Adicionar morador deste apto": mesmos campos do topo; papel sempre Condômino/Morador. */
function AdicionarMoradorApto({ apto, cid, inval, total, qtd }: {
  apto: string; cid: number; inval: () => void; total: number; qtd: number;
}) {
  const [aberto, setAberto] = useState(false);
  const [nome, setNome] = useState(""); const [email, setEmail] = useState("");
  const [telefone, setTelefone] = useState(""); const [senha, setSenha] = useState("");
  const [relacao, setRelacao] = useState("morador");
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const criar = useMutation({
    mutationFn: () => api.post<{ ja_existia?: boolean }>(`/condominios/${cid}/equipe`,
      { nome, email, senha, telefone, papel: "morador", unidade: apto, relacao }),
    onSuccess: (r) => {
      setAviso(r?.ja_existia ? `${email.trim().toLowerCase()} já tinha cadastro no SíndicoApp: foi incluído, mas continua entrando com a senha que já usava (a senha digitada não foi usada). Para trocar, use "Redefinir senha" no cartão dele.` : null);
      setNome(""); setEmail(""); setTelefone(""); setSenha(""); setErro(null); setAberto(false); inval();
    },
    onError: (e) => setErro(e instanceof ApiError ? e.message : "Não foi possível adicionar."),
  });
  return (
    <div className="mt-3 border-t border-slate-100 pt-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-slate-500 inline-flex items-center gap-1">
          <Users size={13} /> Moradores do apto {apto}: <b>{total}</b>
          {qtd > 0 && <span className={total > qtd ? "text-amber-700" : ""}> (cadastro informa {qtd})</span>}
        </span>
        {!aberto && (
          <button onClick={() => { setAberto(true); setErro(null); setAviso(null); }}
            className="inline-flex items-center gap-1 text-xs text-brand-700 border border-brand-300 hover:bg-brand-50 rounded-md px-2 py-1">
            <UserPlus size={13} /> Adicionar morador deste apto
          </button>
        )}
      </div>
      {aberto && (
        <div className="mt-2">
          <div className="grid grid-cols-1 sm:grid-cols-6 gap-2">
            <select value={relacao} onChange={(e) => setRelacao(e.target.value)} title="Relação com o apartamento"
              className="rounded-lg border border-slate-300 px-2 py-2 text-sm bg-white">
              {RELACOES.map((r) => <option key={r.chave} value={r.chave}>{r.rotulo}</option>)}
            </select>
            <input placeholder="Nome" value={nome} onChange={(e) => setNome(e.target.value)} autoFocus
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-brand-500" />
            <input placeholder="E-mail" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-brand-500" />
            <input placeholder="Telefone (opcional)" value={telefone} onChange={(e) => setTelefone(e.target.value)}
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-brand-500" />
            <input placeholder="Senha inicial" type="text" value={senha} onChange={(e) => setSenha(e.target.value)}
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-brand-500" />
            <div className="flex gap-1.5">
              <button disabled={!nome || !email || !senha || criar.isPending} onClick={() => criar.mutate()}
                className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg bg-brand-700 text-white px-3 py-2 text-sm font-medium hover:bg-brand-800 disabled:opacity-50">
                {criar.isPending ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Adicionar
              </button>
              <button onClick={() => { setAberto(false); setErro(null); }} title="Cancelar"
                className="rounded-lg border border-slate-300 px-2 text-slate-500 hover:bg-slate-50"><X size={15} /></button>
            </div>
          </div>
          <p className="text-[11px] text-slate-500 mt-1.5">
            Entra no apto {apto} e recebe os avisos e as notificações do apartamento. <b>Proprietário</b> vota
            e vê a prestação de contas; <b>inquilino</b> e <b>morador</b> usam o dia a dia (visitas, encomendas,
            reservas, boleto) e votam só com <b>procuração</b> do proprietário (registrada em Votações).
            Um voto por apartamento.
          </p>
          {erro && <div className="mt-1.5 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded px-3 py-2">{erro}</div>}
        </div>
      )}
      {aviso && <div className="mt-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">{aviso}</div>}
    </div>
  );
}

function MembroCard({ m, cid, papeis, funcs, inval, rodape }: {
  m: Membro; cid: number; papeis: Papel[]; funcs: Func[]; inval: () => void;
  /** 0.1.82: conteúdo extra no pé do cartão (ex.: adicionar morador do apto). */
  rodape?: React.ReactNode;
}) {
  const { isDono } = usePermissoes();
  const [abrirPerm, setAbrirPerm] = useState(false);
  const [novaSenha, setNovaSenha] = useState<string | null>(null);
  const [novoEmail, setNovoEmail] = useState<string | null>(null);
  const [novoTelefone, setNovoTelefone] = useState<string | null>(null);

  const trocarPapel = useMutation({
    mutationFn: (papel: string) => api.put(`/condominios/${cid}/equipe/${m.id}/papel`, { papel }),
    onSuccess: inval,
  });
  const resetSenha = useMutation({
    mutationFn: (senha: string) => api.post(`/condominios/${cid}/equipe/${m.id}/senha`, { senha }),
    onSuccess: () => { setNovaSenha(null); },
  });
  const remover = useMutation({
    mutationFn: () => api.delete(`/condominios/${cid}/equipe/${m.id}`),
    onSuccess: inval,
  });
  const concessao = useMutation({
    mutationFn: (v: { funcionalidade: string; pode_ler: boolean | null; pode_escrever: boolean | null }) =>
      api.put(`/condominios/${cid}/equipe/${m.id}/concessoes`, v),
    onSuccess: inval,
  });
  const [emailErro, setEmailErro] = useState<string | null>(null);
  const trocarEmail = useMutation({
    mutationFn: (email: string) => api.post(`/condominios/${cid}/equipe/${m.id}/email`, { email }),
    onSuccess: () => { setNovoEmail(null); setEmailErro(null); inval(); },
    onError: (e) => setEmailErro(e instanceof ApiError ? e.message : "Não foi possível trocar o e-mail."),
  });
  const trocarRelacao = useMutation({
    mutationFn: (relacao: string) => api.put(`/condominios/${cid}/equipe/${m.id}/relacao`, { relacao }),
    onSuccess: inval,
  });
  const reativar = useMutation({
    mutationFn: () => api.post(`/condominios/${cid}/equipe/${m.id}/reativar`, {}),
    onSuccess: inval,
  });
  const trocarTelefone = useMutation({
    mutationFn: (telefone: string) => api.post(`/condominios/${cid}/equipe/${m.id}/telefone`, { telefone }),
    onSuccess: () => { setNovoTelefone(null); inval(); },
  });

  // O DONO pode gerir o síndico (inclusive trocar e-mail e remover). O síndico é que
  // não mexe em Dono nem em outro síndico. O Dono nunca se auto-remove por aqui.
  const bloqueado = m.is_dono || (m.papel === "sindico" && !isDono);

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-semibold text-slate-900 flex items-center gap-2 flex-wrap">
            {m.nome}
            {m.apto && <span className="text-[10px] font-bold text-slate-600 bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5">Apto {m.apto}</span>}
            {m.apto && m.relacao && SELO_RELACAO[m.relacao] && (
              <span title={SELO_RELACAO[m.relacao].dica}
                className={`text-[10px] font-bold border rounded px-1.5 py-0.5 ${SELO_RELACAO[m.relacao].cls}`}>
                {SELO_RELACAO[m.relacao].txt}
              </span>
            )}
            {m.nao_reside && (
              <span title="Proprietário de apartamento alugado: não usa a portaria (visitas/encomendas) deste apto."
                className="text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">NÃO RESIDE</span>
            )}
            {!m.ativo && (
              <span title="Conta desativada: não consegue entrar (o login responde senha inválida). Só o Dono reativa."
                className="text-[10px] font-bold text-rose-700 bg-rose-50 border border-rose-200 rounded px-1.5 py-0.5">DESATIVADA</span>
            )}
            {!m.ativo && isDono && (
              <button onClick={() => reativar.mutate()} disabled={reativar.isPending}
                className="inline-flex items-center gap-1 text-[11px] text-emerald-700 border border-emerald-300 hover:bg-emerald-50 rounded px-1.5 py-0.5 disabled:opacity-50">
                {reativar.isPending ? <Loader2 size={11} className="animate-spin" /> : <Check size={11} />} Reativar
              </button>
            )}
            {m.is_dono && <span className="text-[10px] font-bold text-brand-700 bg-brand-50 border border-brand-200 rounded px-1.5 py-0.5">DONO</span>}
            {m.papel === "sindico" && <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5">SÍNDICO</span>}
            {m.whatsapp && (
              <span title="Autorizou receber os avisos também pelo WhatsApp (no Portal)."
                className="text-[10px] font-bold text-green-700 bg-green-50 border border-green-300 rounded px-1.5 py-0.5">WHATSAPP</span>
            )}
          </div>
          <div className="text-xs text-slate-500">{m.email}</div>
          {/* Telefone (opcional) — visível e editável para TODOS os membros. */}
          <div className="text-xs text-slate-500 flex items-center gap-1.5 mt-0.5">
            <Phone size={12} className="text-slate-400" />
            {novoTelefone === null ? (
              <>
                <span>{m.telefone || <span className="text-slate-400">sem telefone</span>}</span>
                <button onClick={() => setNovoTelefone(m.telefone || "")}
                  className="text-brand-700 hover:underline">{m.telefone ? "alterar" : "adicionar"}</button>
                {(() => {
                  // Aviso: telefone do acesso DIFERENTE do que está no cadastro de
                  // moradores (compara só os dígitos, ignora () - espaço). Opção 1:
                  // não sobrescreve sozinho; só sinaliza para o gestor decidir.
                  const so = (s?: string) => (s || "").replace(/\D/g, "");
                  const cad = so(m.telefone_cadastro);
                  const atual = so(m.telefone);
                  if (cad && atual && cad !== atual) {
                    return (
                      <span
                        title={`No cadastro de moradores está ${m.telefone_cadastro}. Clique em "alterar" se quiser usar o do cadastro.`}
                        className="inline-flex items-center gap-1 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5"
                      >
                        <AlertTriangle size={11} /> ≠ cadastro: {m.telefone_cadastro}
                      </span>
                    );
                  }
                  return null;
                })()}
              </>
            ) : (
              <span className="inline-flex items-center gap-1">
                <input autoFocus value={novoTelefone} onChange={(e) => setNovoTelefone(e.target.value)}
                  placeholder="Telefone (opcional)" className="rounded-md border border-slate-300 px-2 py-1 text-xs w-40 outline-none focus:border-brand-500" />
                <button disabled={trocarTelefone.isPending} onClick={() => trocarTelefone.mutate(novoTelefone)}
                  className="inline-flex items-center gap-1 text-xs bg-brand-700 text-white rounded-md px-2 py-1 hover:bg-brand-800 disabled:opacity-40">
                  {trocarTelefone.isPending ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Salvar
                </button>
                <button onClick={() => setNovoTelefone(null)} className="text-slate-400 hover:text-slate-700"><X size={13} /></button>
              </span>
            )}
          </div>
        </div>
        {!bloqueado && (
          <button onClick={() => { if (confirm(`Remover ${m.nome} deste condomínio?`)) remover.mutate(); }}
            className="text-slate-400 hover:text-rose-600" title="Remover deste condomínio"><Trash2 size={16} /></button>
        )}
      </div>

      {!bloqueado && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="text-xs text-slate-500">Papel:</span>
          <select value={m.papel} onChange={(e) => trocarPapel.mutate(e.target.value)}
            className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm bg-white">
            {papeis.map((p) => <option key={p.chave} value={p.chave}>{p.rotulo}</option>)}
          </select>
          {m.apto && (
            <>
              <span className="text-xs text-slate-500">No apto:</span>
              <select value={m.relacao || "morador"} onChange={(e) => trocarRelacao.mutate(e.target.value)}
                title="Proprietário vota e vê a prestação de contas; inquilino e morador votam só com procuração."
                className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm bg-white">
                {RELACOES.map((r) => <option key={r.chave} value={r.chave}>{r.rotulo}</option>)}
              </select>
            </>
          )}

          {novaSenha === null ? (
            <button onClick={() => setNovaSenha("")}
              className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-brand-700 border border-slate-200 hover:border-brand-300 rounded-md px-2 py-1">
              <Lock size={13} /> Redefinir senha
            </button>
          ) : resetSenha.isSuccess ? (
            <span className="inline-flex items-center gap-1 text-xs text-emerald-700"><Check size={14} /> Senha trocada</span>
          ) : (
            <span className="inline-flex items-center gap-1">
              <input type="text" autoFocus value={novaSenha} onChange={(e) => setNovaSenha(e.target.value)}
                placeholder="Nova senha" className="rounded-md border border-slate-300 px-2 py-1 text-sm w-36 outline-none focus:border-brand-500" />
              <button disabled={!novaSenha || resetSenha.isPending} onClick={() => resetSenha.mutate(novaSenha)}
                className="inline-flex items-center gap-1 text-xs bg-brand-700 text-white rounded-md px-2 py-1 hover:bg-brand-800 disabled:opacity-40">
                {resetSenha.isPending ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Salvar
              </button>
              <button onClick={() => setNovaSenha(null)} className="text-slate-400 hover:text-slate-700"><X size={14} /></button>
            </span>
          )}

          {novoEmail === null ? (
            <button onClick={() => { setNovoEmail(m.email); setEmailErro(null); }}
              className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-brand-700 border border-slate-200 hover:border-brand-300 rounded-md px-2 py-1">
              <Mail size={13} /> Trocar e-mail
            </button>
          ) : (
            <span className="inline-flex items-center gap-1">
              <input type="email" autoFocus value={novoEmail} onChange={(e) => setNovoEmail(e.target.value)}
                placeholder="novo@email.com" className="rounded-md border border-slate-300 px-2 py-1 text-sm w-52 outline-none focus:border-brand-500" />
              <button disabled={!novoEmail || trocarEmail.isPending} onClick={() => trocarEmail.mutate(novoEmail)}
                className="inline-flex items-center gap-1 text-xs bg-brand-700 text-white rounded-md px-2 py-1 hover:bg-brand-800 disabled:opacity-40">
                {trocarEmail.isPending ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Salvar
              </button>
              <button onClick={() => { setNovoEmail(null); setEmailErro(null); }} className="text-slate-400 hover:text-slate-700"><X size={14} /></button>
            </span>
          )}

          <button onClick={() => setAbrirPerm((v) => !v)}
            className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-brand-700 border border-slate-200 hover:border-brand-300 rounded-md px-2 py-1">
            <SlidersHorizontal size={13} /> Permissões
          </button>
        </div>
      )}
      {emailErro && <div className="mt-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1">{emailErro}</div>}

      {abrirPerm && !bloqueado && (
        <div className="mt-3 border-t border-slate-100 pt-3">
          <p className="text-[12px] text-slate-500 mb-2 flex items-center gap-1.5">
            <ShieldCheck size={14} className="text-brand-600" />
            Exceções desta pessoa (valem só para ela, sobrepondo o papel). Deixe em
            <b> Padrão</b> para seguir o papel; use <b>Bloquear</b> só para tirar acesso
            de propósito.
          </p>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-[11px] uppercase tracking-wider text-slate-500">
                  <th className="text-left p-1.5">Área</th>
                  <th className="p-1.5 text-left">Acesso desta pessoa</th>
                </tr>
              </thead>
              <tbody>
                {funcs.map((f) => {
                  const c = m.concessoes[f.chave];
                  // Estado atual: "padrao" (sem exceção) | "ver" | "editar" | "bloquear".
                  const estado =
                    !c || (c.pode_ler == null && c.pode_escrever == null) ? "padrao"
                      : c.pode_ler === false ? "bloquear"
                        : c.pode_escrever ? "editar" : "ver";
                  const aplicar = (v: string) => {
                    const mapa: Record<string, { pode_ler: boolean | null; pode_escrever: boolean | null }> = {
                      padrao: { pode_ler: null, pode_escrever: null },   // remove a exceção
                      ver: { pode_ler: true, pode_escrever: false },
                      editar: { pode_ler: true, pode_escrever: true },
                      bloquear: { pode_ler: false, pode_escrever: false },
                    };
                    concessao.mutate({ funcionalidade: f.chave, ...mapa[v] });
                  };
                  return (
                    <tr key={f.chave} className="border-t border-slate-50">
                      <td className="p-1.5 text-slate-800">{f.rotulo}</td>
                      <td className="p-1.5">
                        <select
                          value={estado}
                          onChange={(e) => aplicar(e.target.value)}
                          className="rounded-md border border-slate-300 px-2 py-1 text-sm bg-white"
                        >
                          <option value="padrao">Padrão do papel</option>
                          <option value="ver">Só ver</option>
                          <option value="editar">Ver e editar</option>
                          <option value="bloquear">Bloquear</option>
                        </select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="text-[11px] text-slate-400 mt-2">Em <b>Padrão do papel</b>, a pessoa segue exatamente o que o papel dela permite (sem exceção).</p>
        </div>
      )}
      {rodape}
    </div>
  );
}
